import FilterByDate from '@efex-components/filters/FilterByDate';
import PaymentModal from '@efex-components/payables/PaymentModal';
import { PaymentFormProvider } from '@efex-components/payables/PaymentModal/context';
import SearchTools from '@efex-components/searchTools/SearchTools';
import { QueryParams } from '@efex-services/types';
import { Button } from '@efexteam/components';
import { Actions, Can, Subjects, useRole } from '@efexteam/permission-manager';
import useBoolean from '@main-hooks/useBoolean';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  selectBusiness,
  selectProfileData,
} from '@pages/profile/store/selectors';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/store';
import { fetchExchangeCurrencies } from '@/modules/rate/currencies/store';
import InvoiceDateModal from '../InvoiceDateModal';
import { fetchPayments } from '../store/payments';

const PaymentsHeader = (): React.ReactElement => {
  const navigate = useNavigate();
  const company = useSelector(selectBusiness);
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const userProfile = useSelector(selectProfileData);
  const [params, setParams] = useState<QueryParams>({});

  const [
    isPaymentOutModalOpen,
    { setFalse: hideIsPaymentOutModal, setTrue: showisPaymentOutModal },
  ] = useBoolean(false);
  const [
    InvoiceDateModalOpen,
    { setFalse: hideInvoiceDateModal, setTrue: showInvoiceDateModal },
  ] = useBoolean(false);

  const [showingFilters, { setFalse: removeFilters, setTrue: showFilters }] =
    useBoolean(false);

  const dispatch = useAppDispatch();
  const { isPaymentAnalyst } = useRole();

  const openPopupDocumentKYB = () => {
    navigate('/onboarding');
  };

  const handleCloseInvoiceDate = () => {
    hideInvoiceDateModal();
  };

  const filterByDates = (values: any) => {
    const { dateFrom, dateTo } = values;
    setParams({ dateFrom, dateTo });
  };

  const handleFilters = () => {
    if (showingFilters) {
      removeFilters();
    } else {
      showFilters();
    }
  };

  const openPopupMakeNewPayment = () => {
    if (!userProfile?.isFullRegister) {
      navigate('/onboarding');
      return;
    }
    showisPaymentOutModal();
  };

  const openPopupAccountState = () => {
    if (!userProfile?.isFullRegister) {
      openPopupDocumentKYB();
      return;
    }
    showInvoiceDateModal();
  };

  const fetchPaymentsGlobal = () => {
    if (userProfile?.company.enterpriseId) {
      dispatch(
        fetchPayments({
          enterpriseId: Number(userProfile.company.enterpriseId),
          params: {
            ...params,
          },
        })
      );
    }
  };

  const handleCloseRequestPayment = () => {
    hideIsPaymentOutModal();
    fetchPaymentsGlobal();
    dispatch(fetchExchangeCurrencies(company?.enterpriseId || '0'));
  };

  useEffect(() => {
    fetchPaymentsGlobal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <>
      <Stack
        direction={{ xs: 'column-reverse', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="flex-end"
          spacing={2}
          className="mb-5 md:min-w-[550px]"
        >
          <SearchTools
            onChange={(search) => setParams({ ...params, search })}
          />
          {!matches && (
            <Button
              appearance="primary"
              className="!mb-1 w-full max-w-40"
              onClick={handleFilters}
            >
              {!showingFilters ? t('addFilters') : t('hideFilters')}
            </Button>
          )}
          {matches && (
            <Button
              appearance="primary"
              size="small"
              className="!mb-1"
              onClick={handleFilters}
            >
              {!showingFilters ? <FilterAltIcon /> : <FilterAltOffIcon />}
            </Button>
          )}
        </Stack>
        <Box
          className={clsx(
            'flex align-center items-end justify-between gap-4',
            matches ? 'my-3' : ''
          )}
        >
          {!userProfile?.isSubaccount && (
            <Can action={Actions.CREATE} subject={Subjects.ADD_FUNDS}>
              <Button
                variant="outlined"
                appearance="primary"
                className="!mt-[0px] md:mt-0 m-0 w-[140px] md:w-[160px]"
                onClick={openPopupAccountState}
              >
                {t('InvoiceExport') || ''}
              </Button>
            </Can>
          )}
          <Can action={Actions.CREATE} subject={Subjects.PAYMENTS}>
            <Box className="button !mt-[0px]">
              <Button
                appearance="primary"
                className="m-0"
                onClick={openPopupMakeNewPayment}
                disabled={!userProfile?.canUserWithdraw}
              >
                {t(isPaymentAnalyst ? 'configureNewPayment' : 'makeNewPayment')}
              </Button>
            </Box>
          </Can>
        </Box>
      </Stack>
      {showingFilters && (
        <Box>
          <FilterByDate onChange={filterByDates} />
        </Box>
      )}
      {isPaymentOutModalOpen && (
        <PaymentFormProvider>
          <PaymentModal open onClose={handleCloseRequestPayment} />
        </PaymentFormProvider>
      )}
      {InvoiceDateModalOpen && (
        <InvoiceDateModal open onClose={handleCloseInvoiceDate} />
      )}
    </>
  );
};

export default PaymentsHeader;
